import {type ClassValue, clsx} from "clsx";
import {format} from "date-fns";
import {twMerge} from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const numberWithCommas = (x: string | number) => {
  const _x = x.toString().replace(",", "").replace(/\D/g, "");
  return _x.toLocaleString();
};

export function numberToHuman(val: number): string {
  if (val < 1000) {
    return val.toString();
  } else if (val < 1000000) {
    return (val / 1000).toFixed(0) + "K";
  } else if (val < 1000000000) {
    return (val / 1000000).toFixed(0) + "M";
  } else {
    return (val / 1000000000).toFixed(0) + "B";
  }
}

export const formatCurrency = (amount: number): string => {
  return `$${(amount / 100).toLocaleString(undefined, { minimumFractionDigits: (amount % 100 === 0 ? 0 : 2)})}`;
};

export const replaceNonAlphaNumeric = (input: string, replacement = "") => {
  const regex = /[^a-zA-Z0-9]/g;
  return input.trim().replace(regex, replacement);
};

export const formatDateString = (date?: string): string | null => {
  if (!date) return null;
  const f = "P";
  const d = format(date, f);
  return `${d}`;
};

export const formatTimeString = (date?: string): string | null => {
  if (!date) return null;
  const f = "p";
  const t = format(date, f);
  return `${t}`;
};
export const humanizeName = (name: string) => {
  if(!name) return "";
  let working = name.replaceAll("_", " ").replaceAll("-", " ");
  const extensionPos = working.lastIndexOf(".");
  if(extensionPos > 0){
    working = working.substring(0, extensionPos);
  }
  return working
}

export interface SocialMediaInfo {
  username: string;
  platform: string;
}

export function extractSocialMediaInfo(url: string): SocialMediaInfo | null {
  try {
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    const urlObj = new URL(fullUrl);
    const hostname = urlObj.hostname.toLowerCase().replace('www.', '');
    const pathname = urlObj.pathname.substring(1);

    // Platform-specific parsing
    const platformParsers: Record<string, (path: string) => string | null> = {
      'facebook.com': (path) => {
        // Handle /profile.php?id=username
        if (path.startsWith('profile.php')) {
          const params = new URLSearchParams(urlObj.search);
          return params.get('id');
        }
        // Handle /username
        const match = path.match(/^([^\/\?]+)/);
        return match ? match[1] : null;
      },

      'instagram.com': (path) => {
        const match = path.match(/^(?:u\/)?([^\/\?]+)/);
        return match ? match[1] : null;
      },

      'youtube.com': (path) => {
        const match = path.match(/^(?:c\/|channel\/|user\/)?([^\/\?]+)/);
        return match ? match[1] : null;
      },

      'twitter.com': (path) => {
        const match = path.match(/^([^\/\?]+)/);
        return match ? match[1] : null;
      },

      'x.com': (path) => {
        const match = path.match(/^([^\/\?]+)/);
        return match ? match[1] : null;
      },

      'linkedin.com': (path) => {
        const match = path.match(/^(?:in\/|company\/)?([^\/\?]+)/);
        return match ? match[1] : null;
      },

      'tiktok.com': (path) => {
        const match = path.match(/^@?([^\/\?]+)/);
        return match ? match[1] : null;
      }
    };

    const parser = platformParsers[hostname];
    if (parser) {
      const username = parser(pathname);
      var platform = hostname;
      if (hostname.includes(".")) {
        platform = hostname.split(".")[0];
      }
      if (username && username.length > 0 && username.length < 100) {
        return {
          username,
          platform: platform
        };
      }
    }

    // Fallback: generic parsing
    const username = extractSocialMediaUsername(url);
    if (username) {
      return {
        username,
        platform: hostname
      };
    }

    return null;
  } catch (error) {
    return null;
  }
}

export function extractSocialMediaUsername(url: string): string | null {
  try {
    // Handle URLs without protocol
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    const urlObj = new URL(fullUrl);

    // Get the pathname and remove leading slash
    const pathname = urlObj.pathname.substring(1);

    // Common patterns for different social media platforms
    const patterns = [
      // Facebook: /username or /profile.php?id=username
      /^([^\/\?]+)(?:\/.*)?$/,
      // Instagram: /u/username or /username
      /^(?:u\/)?([^\/\?]+)(?:\/.*)?$/,
      // YouTube: /username or /c/username or /channel/username
      /^(?:c\/|channel\/|user\/)?([^\/\?]+)(?:\/.*)?$/,
      // Twitter/X: /username
      /^([^\/\?]+)(?:\/.*)?$/,
      // LinkedIn: /in/username or /company/username
      /^(?:in\/|company\/)?([^\/\?]+)(?:\/.*)?$/,
      // TikTok: /@username
      /^@?([^\/\?]+)(?:\/.*)?$/,
      // Generic pattern: extract first segment
      /^([^\/\?]+)(?:\/.*)?$/
    ];

    // Skip common non-username paths
    const skipPaths = ['www', 'mobile', 'm', 'api', 'about', 'help', 'support', 'login', 'register'];

    if (!pathname || skipPaths.includes(pathname.toLowerCase())) {
      return null;
    }

    // Try each pattern
    for (const pattern of patterns) {
      const match = pathname.match(pattern);
      if (match && match[1]) {
        const username = match[1];

        // Additional validation: username shouldn't be a common path
        if (!skipPaths.includes(username.toLowerCase()) &&
            username.length > 0 &&
            username.length < 100) { // reasonable length limit
          return username;
        }
      }
    }

    return null;
  } catch (error) {
    // Invalid URL
    return null;
  }
}

export default cn;
